import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  SxProps,
  Theme,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DeleteIcon, EditIcon, UploadIcon } from "components";
import toast from "react-hot-toast";
import { disable } from "mixpanel-browser";

interface ProfileUploadProps {
  onAttachFile: (file: File) => void;
  url: string;
  sx?: SxProps<Theme>;
  onImgFileDelete?: () => void;
  loading?: boolean;
  profilePicture?: boolean;
  disabled?: boolean;
  customPlaceholder?: ({
    disabled,
    onClick,
  }: {
    disabled: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  imageSizeLimit?: number; // in bytes
  deletable?: boolean;
  objectFit?: any;
}
const ProfileUploadComponent = ({
  onAttachFile,
  url,
  sx,
  onImgFileDelete,
  loading = false,
  profilePicture = false,
  disabled = false,
  customPlaceholder,
  imageSizeLimit = 10485760, // in bytes
  deletable = true,
  objectFit = "cover",
}: ProfileUploadProps) => {
  const inputRef = useRef();
  const theme = useTheme();
  const [image, setImage] = useState<any>(null);

  useEffect(() => {
    if (url) {
      setImage(url);
    } else {
      setImage(null);
    }
  }, [url]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    const file = event.target.files?.[0];
    console.log("file picked:", file);
    if (file) {
      const reader = new FileReader();

      // Check if file is an image but not a gif
      const isImage = file.type.startsWith("image/");
      const isGif = file.type === "image/gif";
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

      if (!isImage) {
        return toast.error("Please upload an image file");
      }

      if (isGif || !allowedTypes.includes(file.type)) {
        return toast.error("Only JPEG, JPG, and PNG formats are allowed");
      }

      reader.readAsDataURL(file);
      if (file.size < imageSizeLimit) {
        onAttachFile(file); // Call onAttachFile function with the selected file
      } else {
        const sizeInMB = imageSizeLimit / 1024 / 1024;
        toast.error(
          `Please upload an image with size less than ${sizeInMB.toFixed(0)}${
            sizeInMB < 1 ? "KB" : "MB"
          }`,
        );
      }
    }
  };
  const handleImageDelete = () => {
    if (onImgFileDelete) {
      onImgFileDelete();
    }
  };
  if (loading) {
    return (
      <Stack
        sx={{
          width: "90px",
          height: "90px",
          aspectRatio: 1,
          borderRadius: "100%",
          justifyContent: "center",
          alignItems: "center",
          background: "#FFF",
          border: `1px dashed ${theme.palette.primary.main}`,
          ...sx,
        }}
      >
        <CircularProgress size={"30px"} />
      </Stack>
    );
  }
  return (
    <>
      <input
        alt=""
        // @ts-ignore
        style={{ display: "none" }}
        accept="image/jpeg,image/jpg,image/png"
        id="profile-upload"
        type="file"
        onChange={handleImageChange}
        // @ts-ignore
        ref={inputRef}
        aria-label="Upload profile image"
      />

      {image ? (
        <Stack
          sx={{
            width: "90px",
            height: "90px",
            aspectRatio: 1,
            borderRadius: "100%",
            overflow: "hidden",
            position: "relative",
            ":hover": {
              "& .overlay": !disabled
                ? {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    visibility: "visible",
                  }
                : {},
            },

            ...sx,
          }}
        >
          <Stack
            className="overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              visibility: "hidden",
              flexDirection: "row",
              gap: "6px",
              "& .icon-button": {
                width: "24px",
                height: "24px",
                backgroundColor: "white",
                color: theme.palette.lightBg.high,
                borderRadius: "2px",
                ":hover": !disabled
                  ? {
                      backgroundColor: "white",
                    }
                  : {},
              },
            }}
          >
            <IconButton
              className="icon-button"
              onClick={() => {
                // @ts-ignore
                !disabled && inputRef?.current && inputRef?.current?.click();
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {deletable && (
              <IconButton
                className="icon-button"
                onClick={() => !disabled && handleImageDelete()}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          <img
            style={{ objectFit: objectFit, objectPosition: "center" }}
            src={image}
            alt="profile"
            width="100%"
            height="100%"
          />
        </Stack>
      ) : customPlaceholder ? (
        customPlaceholder({
          disabled,
          onClick: () => {
            // @ts-ignore
            inputRef?.current?.click?.();
          },
        })
      ) : (
        <Stack
          sx={{
            width: "90px",
            height: "90px",
            aspectRatio: 1,
            backgroundColor: disabled
              ? theme.palette.neutral["005"]
              : "#E1FAFC",
            border: `1px dashed ${
              disabled
                ? theme.palette.neutral["020"]
                : theme.palette.primary.main
            }`,
            borderRadius: "100%",
            justifyContent: "center",
            alignItems: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            ...sx,
          }}
          // @ts-ignore
          onClick={() => !disabled && inputRef?.current?.click?.()}
        >
          <UploadIcon
            htmlColor={
              disabled ? theme.palette.lightBg.low : theme.palette.primary.main
            }
          />
          <Typography variant="body-small">Upload</Typography>
        </Stack>
      )}
    </>
  );
};

export { ProfileUploadComponent };
